import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { novels } from '@/mocks/novels';

interface SearchBoxProps {
  variant?: 'default' | 'hero';
}

export default function SearchBox({ variant = 'default' }: SearchBoxProps) {
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef<HTMLDivElement>(null);

  const suggestions = useMemo(() => {
    const key = keyword.trim();
    if (!key) return [];
    return novels
      .filter(
        (n) => n.title.includes(key) || n.author.includes(key) || n.category.includes(key),
      )
      .slice(0, 6);
  }, [keyword]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const submit = (value: string) => {
    const key = value.trim();
    setOpen(false);
    navigate(key ? `/category?q=${encodeURIComponent(key)}` : '/category');
  };

  const isHero = variant === 'hero';

  return (
    <div ref={boxRef} className="relative w-full">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(keyword);
        }}
        className={`flex items-center gap-2 rounded-full border bg-background-50 pl-4 pr-1.5 py-1.5 transition-colors ${
          isHero
            ? 'border-background-200 shadow-sm'
            : 'border-background-300'
        } focus-within:border-primary-400`}
      >
        <i className="ri-search-line text-foreground-400 text-base w-5 h-5 flex items-center justify-center"></i>
        <input
          type="text"
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={isHero ? '搜索书名、作者或题材，开启阅读之旅' : '搜索小说'}
          className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
        />
        <button
          type="submit"
          className="shrink-0 whitespace-nowrap cursor-pointer rounded-full bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      {open && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1.5 text-left shadow-lg animate-fade-in">
          {suggestions.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => {
                setOpen(false);
                setKeyword('');
                navigate(`/detail/${n.id}`);
              }}
              className="flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-primary-50"
            >
              <img
                src={n.cover}
                alt={n.title}
                className="h-12 w-9 shrink-0 rounded object-cover object-top"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground-900">
                  {n.title}
                </span>
                <span className="mt-0.5 block truncate text-xs text-foreground-500">
                  {n.author} · {n.category}
                </span>
              </span>
              <i className="ri-arrow-right-s-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}